<!-- src/views/new-tip/AssignmentsManager.vue -->
<template>
  <v-container fluid>
    <div class="d-flex align-center mb-4">
      <v-btn icon :to="{ name: 'new-tip.campaigns.show', params: { id } }">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <div class="ml-2">
        <h1 class="text-h5 font-weight-medium">Affectations</h1>
        <span v-if="campaign" class="text-caption grey--text">
          Campagne : {{ campaign.name }} ·
          {{ formatDate(campaign.start_date) }} → {{ formatDate(campaign.end_date) }}
        </span>
      </div>
      <v-spacer />
      <v-btn outlined @click="openImport">
        <v-icon left>mdi-file-excel</v-icon>
        Import Excel
      </v-btn>
      <v-btn color="primary" class="ml-2" :loading="saving" @click="save">
        <v-icon left>mdi-content-save</v-icon>
        Sauvegarder
      </v-btn>
    </div>

    <v-alert v-if="dirty" type="info" outlined dense class="mb-3">
      Des modifications non sauvegardées sont en attente.
    </v-alert>

    <v-card outlined :loading="loading">
      <v-card-text>
        <new-tip-objectives-gains-table
          v-model="rows"
          :supervisors="supervisors"
          :known-salepoints="knownSalepoints"
          @row-delete="onRowDelete"
        />
      </v-card-text>
    </v-card>

    <!-- Dialog Import Excel -->
    <v-dialog v-model="importDialog" max-width="600">
      <v-card>
        <v-card-title>Import Excel</v-card-title>
        <v-card-text>
          <p class="text-body-2 grey--text">
            Le fichier doit contenir les colonnes : <code>pos_code</code>,
            <code>supervisor_id</code> ou <code>supervisor_phone</code>,
            puis les colonnes optionnelles d'objectifs et gains.
          </p>
          <v-file-input
            v-model="importFile" label="Fichier Excel"
            accept=".xlsx,.xls,.csv" prepend-icon="mdi-file-excel"
            outlined dense show-size
          />

          <v-alert v-if="importReport" :type="importReport.errors.length ? 'warning' : 'success'" outlined dense>
            <div>{{ importReport.summary.created }} créées · {{ importReport.summary.updated }} mises à jour · {{ importReport.summary.skipped }} ignorées</div>
            <ul v-if="importReport.errors.length" class="mt-2 mb-0">
              <li v-for="(err, i) in importReport.errors.slice(0, 10)" :key="i" class="text-caption">
                Ligne {{ err.row || err.index }} : {{ err.message }}
              </li>
            </ul>
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeImport">Fermer</v-btn>
          <v-btn color="primary" :loading="saving" :disabled="!importFile" @click="runImport">
            Importer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { formatDate } from '@/helpers/formatters';
import ObjectivesGainsTable from '@/components/new-tip/ObjectivesGainsTable.vue';
import api from '@/api/newTipApi';

export default {
  name: 'NewTipAssignmentsManager',
  components: { 'new-tip-objectives-gains-table': ObjectivesGainsTable },
  props: { id: { type: [String, Number], required: true } },
  data() {
    return {
      rows: [],
      originalRows: [],
      supervisors: [],
      // POS déjà connus = ceux des affectations chargées depuis le backend.
      // Sert à afficher leur code+nom dans la table sans les rechercher.
      knownSalepoints: [],
      importDialog: false,
      importFile: null,
      importReport: null,
      pendingDeletions: [],
    };
  },
  computed: {
    ...mapState('newTip', { campaign: s => s.currentCampaign, assignments: s => s.assignments }),
    loading() { return this.$store.getters['newTip/isLoading']('assignments'); },
    saving() { return this.$store.getters['newTip/isLoading']('saving'); },
    dirty() {
      return JSON.stringify(this.rows) !== JSON.stringify(this.originalRows)
          || this.pendingDeletions.length > 0;
    },
  },
  methods: {
    ...mapActions('newTip', ['fetchAssignments', 'saveAssignments', 'importAssignments', 'deleteAssignment']),
    formatDate,

    async load() {
      await this.fetchAssignments({ campaignId: this.id });

      // Mappe les affectations pour la table éditable
      this.rows = this.assignments.map(a => ({
        id: a.id,
        salepoint_id: a.salepoint_id,
        supervisor_id: a.supervisor_id,
        gob_objective_qty: a.gob_objective_qty,
        west_objective_qty: a.west_objective_qty,
        gub_objective_qty: a.gub_objective_qty,
        hats_objective_qty: a.hats_objective_qty,
        gob_gain: a.gob_gain,
        west_gain: a.west_gain,
        gub_gain: a.gub_gain,
        hats_gain: a.hats_gain,
        stock_bonus_percentage: a.stock_bonus_percentage,
        visits_count: a.visits_count,
      }));
      this.originalRows = JSON.parse(JSON.stringify(this.rows));

      // Extraire les POS déjà rattachés pour les afficher (code+nom)
      // sans appel API supplémentaire — ils viennent directement de la
      // relation salepoint chargée par le backend.
      this.knownSalepoints = this.assignments
        .map(a => a.salepoint)
        .filter(Boolean);
    },

    async fetchSupervisors() {
      try {
        const { data } = await api.listSupervisors();
        // Réponse : { success: true, data: [ { id, name, phone, ... } ] }
        this.supervisors = (data && data.data) || [];
      } catch (err) {
        console.error('Erreur de chargement supervisors', err);
      }
    },

    onRowDelete(assignmentId) {
      if (assignmentId && !this.pendingDeletions.includes(assignmentId)) {
        this.pendingDeletions.push(assignmentId);
      }
    },

    async save() {
      for (const id of this.pendingDeletions) {
        try { await this.deleteAssignment(id); }
        catch (err) { console.error(`Échec suppression #${id}`, err); }
      }
      this.pendingDeletions = [];

      const valid = this.rows.filter(r => r.salepoint_id && r.supervisor_id);
      if (valid.length === 0) {
        this.$emit('toast', 'Aucune affectation à enregistrer');
        await this.load();
        return;
      }
      const result = await this.saveAssignments({ campaignId: this.id, assignments: valid });
      this.$emit('toast', `${result.summary.created} créées, ${result.summary.updated} mises à jour, ${result.summary.skipped} ignorées`);
      await this.load();
    },

    openImport() { this.importDialog = true; this.importReport = null; this.importFile = null; },
    closeImport() { this.importDialog = false; },

    async runImport() {
      if (!this.importFile) return;
      this.importReport = await this.importAssignments({
        campaignId: this.id,
        file: this.importFile,
      });
      await this.load();
    },
  },

  async mounted() {
    await Promise.all([this.fetchSupervisors(), this.load()]);
  },
};
</script>
